/**
 * RSAEditor Component
 * Edit Responsive Search Ad headlines and descriptions
 */

import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { TrashIcon, CloseIcon } from '@/components/ui/Icons';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { getRSA, updateRSA, type RSATextAsset } from '@/services/api/assets';
import { debug, toast } from '@/utils';
import { RSA_LIMITS } from '@/constants/googleAdsLimits';

interface RSAEditorProps {
  memberUuid: string;
  adGroupId: string;
  adId: string;
  customerId?: string;
  adGroupName?: string;  // Pre-populated ad group name for immediate display
  onSaved: () => void;
  onCancel: () => void;
}

const HEADLINE_LIMIT = RSA_LIMITS.HEADLINE_CHARS;
const DESCRIPTION_LIMIT = RSA_LIMITS.DESCRIPTION_CHARS;
const PATH_LIMIT = 15;
const MIN_HEADLINES = RSA_LIMITS.MIN_HEADLINES;
const MAX_HEADLINES = RSA_LIMITS.MAX_HEADLINES;
const MIN_DESCRIPTIONS = RSA_LIMITS.MIN_DESCRIPTIONS;
const MAX_DESCRIPTIONS = RSA_LIMITS.MAX_DESCRIPTIONS;

// Pin positions are limited regardless of how many headlines/descriptions you have:
// - Headlines: 3 positions (ads show up to 3 headlines)
// - Descriptions: 2 positions (ads show up to 2 descriptions)

export function RSAEditor({
  memberUuid,
  adGroupId,
  adId,
  customerId,
  adGroupName: initialAdGroupName,
  onSaved,
  onCancel,
}: RSAEditorProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // RSA data
  const [headlines, setHeadlines] = useState<RSATextAsset[]>([]);
  const [descriptions, setDescriptions] = useState<RSATextAsset[]>([]);
  const [finalUrls, setFinalUrls] = useState<string[]>([]);
  const [path1, setPath1] = useState<string>('');
  const [path2, setPath2] = useState<string>('');
  const [adGroupName, setAdGroupName] = useState<string>(initialAdGroupName || '');

  // Load RSA data
  useEffect(() => {
    async function loadRSA() {
      setLoading(true);
      setError(null);

      try {
        const response = await getRSA({
          member_uuid: memberUuid,
          ad_group_id: adGroupId,
          ad_id: adId,
          customer_id: customerId,
        });

        if (response.success) {
          setHeadlines(response.headlines || []);
          setDescriptions(response.descriptions || []);
          setFinalUrls(response.final_urls || []);
          setPath1(response.path1 || '');
          setPath2(response.path2 || '');
          setAdGroupName(response.ad_group_name || initialAdGroupName || '');
          debug.log('Loaded RSA data', response);
        } else {
          setError(response.error || 'Failed to load RSA');
        }
      } catch (err) {
        console.error('Error loading RSA:', err);
        setError('Failed to load RSA data');
      } finally {
        setLoading(false);
      }
    }

    loadRSA();
  }, [memberUuid, adGroupId, adId, customerId]);

  // Update headline text
  const handleHeadlineChange = (index: number, text: string) => {
    if (text.length > HEADLINE_LIMIT) return;
    const updated = [...headlines];
    updated[index] = { ...updated[index], text };
    setHeadlines(updated);
  };

  // Update headline pinning
  const handleHeadlinePinChange = (index: number, pinned_field: string | null) => {
    const updated = [...headlines];
    updated[index] = { ...updated[index], pinned_field };
    setHeadlines(updated);
  };

  // Add new headline
  const handleAddHeadline = () => {
    if (headlines.length >= MAX_HEADLINES) {
      toast.warning(`Maximum ${MAX_HEADLINES} headlines allowed`);
      return;
    }
    setHeadlines([...headlines, { text: '', pinned_field: null }]);
  };

  // Remove headline
  const handleRemoveHeadline = (index: number) => {
    if (headlines.length <= MIN_HEADLINES) {
      toast.warning(`Minimum ${MIN_HEADLINES} headlines required`);
      return;
    }
    setHeadlines(headlines.filter((_, i) => i !== index));
  };

  // Update description text
  const handleDescriptionChange = (index: number, text: string) => {
    if (text.length > DESCRIPTION_LIMIT) return;
    const updated = [...descriptions];
    updated[index] = { ...updated[index], text };
    setDescriptions(updated);
  };

  // Update description pinning
  const handleDescriptionPinChange = (index: number, pinned_field: string | null) => {
    const updated = [...descriptions];
    updated[index] = { ...updated[index], pinned_field };
    setDescriptions(updated);
  };

  // Add new description
  const handleAddDescription = () => {
    if (descriptions.length >= MAX_DESCRIPTIONS) {
      toast.warning(`Maximum ${MAX_DESCRIPTIONS} descriptions allowed`);
      return;
    }
    setDescriptions([...descriptions, { text: '', pinned_field: null }]);
  };

  // Remove description
  const handleRemoveDescription = (index: number) => {
    if (descriptions.length <= MIN_DESCRIPTIONS) {
      toast.warning(`Minimum ${MIN_DESCRIPTIONS} descriptions required`);
      return;
    }
    setDescriptions(descriptions.filter((_, i) => i !== index));
  };

  // Handle landing page URL change
  const handleLandingPageChange = (url: string) => {
    setFinalUrls([url]);
  };

  // Validate and save
  const handleSave = async () => {
    // Validate headlines
    const validHeadlines = headlines.filter(h => h.text.trim());
    if (validHeadlines.length < MIN_HEADLINES) {
      toast.error(`At least ${MIN_HEADLINES} headlines are required`);
      return;
    }

    // Validate descriptions
    const validDescriptions = descriptions.filter(d => d.text.trim());
    if (validDescriptions.length < MIN_DESCRIPTIONS) {
      toast.error(`At least ${MIN_DESCRIPTIONS} descriptions are required`);
      return;
    }

    // Check for empty required fields
    if (headlines.some(h => !h.text.trim())) {
      toast.error('Please fill in all headline fields or remove empty ones');
      return;
    }
    if (descriptions.some(d => !d.text.trim())) {
      toast.error('Please fill in all description fields or remove empty ones');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const response = await updateRSA({
        member_uuid: memberUuid,
        ad_group_id: adGroupId,
        ad_id: adId,
        customer_id: customerId,
        headlines,
        descriptions,
        final_urls: finalUrls.length > 0 ? finalUrls : undefined,
        path1,
        path2,
        trigger_data_sync: true,
      });

      if (response.success) {
        toast.success(response.message);
        onSaved();
      } else {
        setError(response.error || 'Failed to update RSA');
        toast.error(response.error || 'Failed to update RSA');
      }
    } catch (err) {
      console.error('Error updating RSA:', err);
      setError('Failed to update RSA');
      toast.error('Failed to update RSA');
    } finally {
      setSaving(false);
    }
  };

  // Error state with no data - show error with back button
  if (error && headlines.length === 0 && !loading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow border max-w-4xl w-full">
        <div className="flex items-start justify-between mb-4 gap-2">
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-semibold text-gray-900">Manage Ad Assets</h2>
            <p className="text-sm text-gray-500">
              RSA in <span className="font-semibold text-gray-700">{adGroupName || 'Ad Group'}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="flex-shrink-0 p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            aria-label="Close editor"
          >
            <CloseIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="text-red-600 mb-4">{error}</div>
        <Button onClick={onCancel} variant="secondary">
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow border max-w-4xl w-full">
      <div className="flex items-start justify-between mb-4 gap-2">
        <div className="min-w-0 flex-1">
          <h2 className="text-xl font-semibold text-gray-900">Manage Ad Assets</h2>
          <p className="text-sm text-gray-500">
            RSA in <span className="font-semibold text-gray-700">{adGroupName || 'Ad Group'}</span>
          </p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="flex-shrink-0 p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          aria-label="Close editor"
        >
          <CloseIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {/* Error message (when we have data but encountered an error) */}
      {!loading && error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Content (only shown when not loading) */}
      {!loading && (
        <>
          {/* Landing Page URL */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Landing Page URL
            </label>
            <Input
              value={finalUrls[0] || ''}
              onChange={(e) => handleLandingPageChange(e.target.value)}
              placeholder="https://example.com/landing-page"
              className="flex-1"
            />
          </div>

          {/* Display Paths */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Display Path
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Optional text that appears in the ad URL (e.g., example.com/<span className="font-medium">path1</span>/<span className="font-medium">path2</span>)
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1 sm:hidden">Path 1</label>
                <Input
                  value={path1}
                  onChange={(e) => {
                    if (e.target.value.length <= PATH_LIMIT) {
                      setPath1(e.target.value);
                    }
                  }}
                  placeholder="path1"
                  maxLength={PATH_LIMIT}
                  aria-label="Display path 1"
                />
                <span className="text-xs text-gray-400">{path1.length}/{PATH_LIMIT}</span>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1 sm:hidden">Path 2</label>
                <Input
                  value={path2}
                  onChange={(e) => {
                    if (e.target.value.length <= PATH_LIMIT) {
                      setPath2(e.target.value);
                    }
                  }}
                  placeholder="path2"
                  maxLength={PATH_LIMIT}
                  aria-label="Display path 2"
                />
                <span className="text-xs text-gray-400">{path2.length}/{PATH_LIMIT}</span>
              </div>
            </div>
          </div>

          {/* Headlines Section */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Headlines ({headlines.length}/{MAX_HEADLINES})
              </label>
              <Button
                onClick={handleAddHeadline}
                variant="ghost"
                size="sm"
                disabled={headlines.length >= MAX_HEADLINES}
                className="self-start sm:self-auto mt-1 sm:mt-0"
              >
                + Add Headline
              </Button>
            </div>
            <p className="text-xs text-gray-500 mb-2">
              {MIN_HEADLINES}-{MAX_HEADLINES} headlines required, max {HEADLINE_LIMIT} characters each
            </p>
            <div className="space-y-3">
              {headlines.map((headline, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-3 sm:border-0 sm:p-0">
                  {/* Input row */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400 w-6 flex-shrink-0">{index + 1}.</span>
                    <Input
                      value={headline.text}
                      onChange={(e) => handleHeadlineChange(index, e.target.value)}
                      placeholder={`Headline ${index + 1}`}
                      maxLength={HEADLINE_LIMIT}
                      className="flex-1"
                      aria-label={`Headline ${index + 1}`}
                    />
                    <span className="text-xs text-gray-400 w-12 flex-shrink-0 hidden sm:block">
                      {headline.text.length}/{HEADLINE_LIMIT}
                    </span>
                    {/* Desktop: inline controls */}
                    <select
                      value={headline.pinned_field || ''}
                      onChange={(e) => handleHeadlinePinChange(index, e.target.value || null)}
                      className="hidden sm:block text-xs border rounded px-2 py-1 min-w-[105px]"
                      title="Pin position"
                      aria-label={`Pin position for headline ${index + 1}`}
                    >
                      <option value="">No pin</option>
                      <option value="HEADLINE_1">1st position</option>
                      <option value="HEADLINE_2">2nd position</option>
                      <option value="HEADLINE_3">3rd position</option>
                    </select>
                    <Button
                      onClick={() => handleRemoveHeadline(index)}
                      variant="ghost"
                      size="sm"
                      disabled={headlines.length <= MIN_HEADLINES}
                      className="hidden sm:flex text-red-500 hover:text-red-700"
                      aria-label={`Remove headline ${index + 1}`}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </Button>
                  </div>
                  {/* Mobile: controls on new line */}
                  <div className="flex items-center justify-between mt-2 pl-8 sm:hidden">
                    <span className="text-xs text-gray-400">
                      {headline.text.length}/{HEADLINE_LIMIT}
                    </span>
                    <div className="flex items-center gap-2">
                      <select
                        value={headline.pinned_field || ''}
                        onChange={(e) => handleHeadlinePinChange(index, e.target.value || null)}
                        className="text-xs border rounded px-2 py-1.5"
                        title="Pin position"
                        aria-label={`Pin position for headline ${index + 1}`}
                      >
                        <option value="">No pin</option>
                        <option value="HEADLINE_1">1st position</option>
                        <option value="HEADLINE_2">2nd position</option>
                        <option value="HEADLINE_3">3rd position</option>
                      </select>
                      <Button
                        onClick={() => handleRemoveHeadline(index)}
                        variant="ghost"
                        size="sm"
                        disabled={headlines.length <= MIN_HEADLINES}
                        className="text-red-500 hover:text-red-700"
                        aria-label={`Remove headline ${index + 1}`}
                      >
                        <TrashIcon className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Descriptions Section */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Descriptions ({descriptions.length}/{MAX_DESCRIPTIONS})
              </label>
              <Button
                onClick={handleAddDescription}
                variant="ghost"
                size="sm"
                disabled={descriptions.length >= MAX_DESCRIPTIONS}
                className="self-start sm:self-auto mt-1 sm:mt-0"
              >
                + Add Description
              </Button>
            </div>
            <p className="text-xs text-gray-500 mb-2">
              {MIN_DESCRIPTIONS}-{MAX_DESCRIPTIONS} descriptions required, max {DESCRIPTION_LIMIT} characters each
            </p>
            <div className="space-y-3">
              {descriptions.map((description, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-3 sm:border-0 sm:p-0">
                  {/* Input row */}
                  <div className="flex items-start gap-2">
                    <span className="text-xs text-gray-400 w-6 pt-2 flex-shrink-0">{index + 1}.</span>
                    <textarea
                      value={description.text}
                      onChange={(e) => handleDescriptionChange(index, e.target.value)}
                      placeholder={`Description ${index + 1}`}
                      maxLength={DESCRIPTION_LIMIT}
                      rows={2}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                      aria-label={`Description ${index + 1}`}
                    />
                    <span className="text-xs text-gray-400 w-12 pt-2 flex-shrink-0 hidden sm:block">
                      {description.text.length}/{DESCRIPTION_LIMIT}
                    </span>
                    {/* Desktop: inline controls */}
                    <select
                      value={description.pinned_field || ''}
                      onChange={(e) => handleDescriptionPinChange(index, e.target.value || null)}
                      className="hidden sm:block text-xs border rounded px-2 py-1 mt-1 min-w-[100px]"
                      title="Pin position"
                      aria-label={`Pin position for description ${index + 1}`}
                    >
                      <option value="">No pin</option>
                      <option value="DESCRIPTION_1">1st position</option>
                      <option value="DESCRIPTION_2">2nd position</option>
                    </select>
                    <Button
                      onClick={() => handleRemoveDescription(index)}
                      variant="ghost"
                      size="sm"
                      disabled={descriptions.length <= MIN_DESCRIPTIONS}
                      className="hidden sm:flex text-red-500 hover:text-red-700 mt-1"
                      aria-label={`Remove description ${index + 1}`}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </Button>
                  </div>
                  {/* Mobile: controls on new line */}
                  <div className="flex items-center justify-between mt-2 pl-8 sm:hidden">
                    <span className="text-xs text-gray-400">
                      {description.text.length}/{DESCRIPTION_LIMIT}
                    </span>
                    <div className="flex items-center gap-2">
                      <select
                        value={description.pinned_field || ''}
                        onChange={(e) => handleDescriptionPinChange(index, e.target.value || null)}
                        className="text-xs border rounded px-2 py-1.5"
                        title="Pin position"
                        aria-label={`Pin position for description ${index + 1}`}
                      >
                        <option value="">No pin</option>
                        <option value="DESCRIPTION_1">1st position</option>
                        <option value="DESCRIPTION_2">2nd position</option>
                      </select>
                      <Button
                        onClick={() => handleRemoveDescription(index)}
                        variant="ghost"
                        size="sm"
                        disabled={descriptions.length <= MIN_DESCRIPTIONS}
                        className="text-red-500 hover:text-red-700"
                        aria-label={`Remove description ${index + 1}`}
                      >
                        <TrashIcon className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Save/Cancel Buttons */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button onClick={onCancel} variant="secondary" disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} variant="primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export default RSAEditor;
